// «Решения» = очередь «Ждёт вас»: всё, что ждёт владельца, одним списком (api /api/waiting) — заявки и советы
// агента, его вопросы из итогов работ, задачи «ждёт вас», батчи на приёмке, правки платформы, лиды антифрода
// (одной строкой) и запросы разрешений из чатов. Сверху — деньги и срок; технические разрешения — внизу,
// свёрнутой группой. История решений — в «Агент → Журнал».
import { Button, Chip, EmptyState, inTime, int, plural, toast, when } from '@autoaff/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useSearch } from '@tanstack/react-router';
import {
  BookCheck,
  Check,
  ChevronRight,
  Clapperboard,
  Code2,
  Globe,
  HelpCircle,
  History,
  Inbox,
  KeyRound,
  Lightbulb,
  ListTodo,
  Megaphone,
  MessageCircle,
  Mic,
  Package,
  Send,
  ShieldAlert,
  Wallet,
} from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { api } from '../api';
import { chatStore } from '../chat/store';
import { useVoiceInput } from '../chat/useVoice';
import {
  answerAgent,
  askOf,
  dropWaiting,
  type PermissionItem,
  useRefreshWaiting,
  useWaiting,
  type WaitingItem,
} from '../shell/waiting';
import { PageHead } from './common';
import { ProposalActions, ProposalCard, TechDetails } from './Proposals';

type Of<K extends WaitingItem['kind']> = Extract<WaitingItem, { kind: K }>;

// ─── разрешения из чатов ────────────────────────────────────────────────
const fileName = (p: string | undefined) => p?.split(/[\\/]/).pop() || undefined;
/** Заголовок документа, который агент хочет сохранить: «# Бриф: 4 креатива» → «Бриф: 4 креатива». */
const docTitle = (content: string | undefined) =>
  content ? /^\s*#{1,3}\s+(.{3,80}?)\s*$/m.exec(content)?.[1] : undefined;
const lowerFirst = (s: string) => s.charAt(0).toLocaleLowerCase('ru') + s.slice(1);
const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n)}…` : s);
const hostOf = (url: string | undefined) => {
  try {
    return url ? new URL(url).hostname.replace(/^www\./, '') : undefined;
  } catch {
    return url;
  }
};
/** Значение параметра для «технически»: строка как есть, остальное — коротко одной строкой. */
const plain = (v: unknown) => clip(typeof v === 'string' ? v : JSON.stringify(v), 400);

/**
 * Что агент хочет сделать — простыми словами («Агент хочет сохранить «Бриф: 4 креатива»», «Агент хочет проверить
 * футаж»); путь, команда и параметры — отдельно, для «Показать технически». Незнакомый инструмент — тоже словами.
 */
export function permissionText(p: PermissionItem): {
  title: string;
  detail: string | null;
  tech: Array<[string, string]>;
} {
  const i = p.input;
  const s = (k: string) => (typeof i[k] === 'string' ? (i[k] as string) : undefined);
  const where = p.offer ? `оффер «${p.offer}»` : null;
  const line = (...xs: Array<string | null | undefined | false>) => xs.filter(Boolean).join(' · ') || null;
  const file = fileName(s('file_path'));
  switch (p.toolName) {
    case 'Write': {
      const doc = docTitle(s('content'));
      return {
        title: doc ? `Агент хочет сохранить «${doc}»` : `Агент хочет сохранить файл ${file ?? ''}`.trim(),
        detail: line(doc && file && `файл ${file}`, where),
        tech: [
          ['Путь', s('file_path') ?? '—'],
          ['Содержимое', clip(s('content') ?? '', 800)],
        ],
      };
    }
    case 'Edit':
    case 'MultiEdit':
      return {
        title: `Агент хочет поправить файл ${file ?? ''}`.trim(),
        detail: where,
        tech: [
          ['Путь', s('file_path') ?? '—'],
          ...(s('old_string')
            ? ([['Было', clip(s('old_string') ?? '', 400)]] as Array<[string, string]>)
            : []),
          ...(s('new_string')
            ? ([['Стало', clip(s('new_string') ?? '', 400)]] as Array<[string, string]>)
            : []),
        ],
      };
    case 'Read':
      return {
        title: `Агент хочет прочитать файл ${file ?? ''}`.trim(),
        detail: where,
        tech: [['Путь', s('file_path') ?? '—']],
      };
    case 'Bash': {
      const why = s('description');
      return {
        title: why ? `Агент хочет ${lowerFirst(why)}` : 'Агент хочет выполнить команду на сервере',
        detail: where,
        tech: [['Команда', s('command') ?? '—']],
      };
    }
    case 'WebFetch':
      return {
        title: `Агент хочет открыть страницу ${hostOf(s('url')) ?? ''}`.trim(),
        detail: s('prompt') ? `Что ищет: ${clip(s('prompt') ?? '', 160)}` : null,
        tech: [['Адрес', s('url') ?? '—']],
      };
    case 'WebSearch':
      return {
        title: `Агент хочет поискать в интернете: «${clip(s('query') ?? '', 80)}»`,
        detail: null,
        tech: [['Запрос', s('query') ?? '—']],
      };
    case 'Task':
    case 'Agent': {
      const what = s('description');
      return {
        title: `Агент хочет поручить часть работы помощнику${what ? `: ${lowerFirst(what)}` : ''}`,
        detail: null,
        tech: [['Задание', clip(s('prompt') ?? what ?? '—', 800)]],
      };
    }
    default: {
      const platform = p.toolName.startsWith('mcp__platform__');
      const name = p.toolName
        .replace(/^mcp__(platform__)?/, '')
        .replace(/__/g, ' → ')
        .replace(/_/g, ' ');
      return {
        title: platform
          ? `Агент хочет выполнить действие платформы «${name}»`
          : `Агент хочет воспользоваться инструментом «${name}»`,
        detail: where,
        tech: [
          ['Инструмент', p.toolName],
          ...Object.entries(i).map(([k, v]): [string, string] => [k, plain(v)]),
        ],
      };
    }
  }
}

function usePermission(p: PermissionItem, onDone: () => void) {
  const [busy, setBusy] = useState<'allow' | 'deny' | null>(null);
  const decide = async (decision: 'allow' | 'deny') => {
    setBusy(decision);
    try {
      await api(`/api/permissions/${p.id}`, { decision });
      toast(decision === 'allow' ? 'Разрешено — агент продолжает' : 'Отказано — агент продолжит без этого');
      onDone();
    } catch (e) {
      toast((e as Error).message);
      setBusy(null);
    }
  };
  return { busy, decide };
}

function PermissionButtons({ p, onDone, size }: { p: PermissionItem; onDone: () => void; size?: 'sm' }) {
  const { busy, decide } = usePermission(p, onDone);
  return (
    <>
      <Button
        variant={size ? 'default' : 'primary'}
        size={size}
        icon={busy === 'allow' ? <span className="spinner" /> : <Check />}
        disabled={!!busy}
        onClick={() => void decide('allow')}
      >
        Разрешить
      </Button>
      <Button size={size} disabled={!!busy} onClick={() => void decide('deny')}>
        Отказать
      </Button>
    </>
  );
}

function PermissionCard({ p, onDone }: { p: PermissionItem; onDone: () => void }) {
  const t = permissionText(p);
  return (
    <article className="card prop">
      <div className="flex items-center gap-2 flex-wrap">
        <Chip>Разрешение в чате</Chip>
        <span className="muted wq-when">ответ нужен {inTime(p.expiresAt)}</span>
      </div>
      <h3 className="prop-title">{t.title}</h3>
      {(t.detail || p.threadTitle) && (
        <p className="wq-ctx">
          {[t.detail, p.threadTitle && `из чата «${clip(p.threadTitle, 70)}»`].filter(Boolean).join(' · ')}
        </p>
      )}
      <TechDetails rows={t.tech} />
      <div className="flex gap-2 flex-wrap items-center">
        <PermissionButtons p={p} onDone={onDone} />
        {p.threadId && (
          <Button
            variant="ghost"
            icon={<MessageCircle />}
            onClick={() => chatStore.open({ threadId: p.threadId })}
            style={{ marginLeft: 'auto' }}
          >
            Открыть чат
          </Button>
        )}
      </div>
    </article>
  );
}

// ─── остальные строки очереди ───────────────────────────────────────────
/** Общая рамка карточки очереди: метка, время, заголовок, подробности и действия. */
function QueueCard({
  chip,
  tone = 'default',
  at,
  title,
  children,
  actions,
}: {
  chip: string;
  tone?: 'default' | 'accent' | 'warning';
  at?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  actions: ReactNode;
}) {
  return (
    <article className="card prop">
      <div className="flex items-center gap-2 flex-wrap">
        <Chip tone={tone}>{chip}</Chip>
        {at && <span className="muted wq-when">{at}</span>}
      </div>
      <h3 className="prop-title">{title}</h3>
      {children}
      <div className="flex gap-2 flex-wrap items-center">{actions}</div>
    </article>
  );
}

function useDismiss(key: string, onDone: () => void) {
  const [busy, setBusy] = useState(false);
  return {
    busy,
    run: async () => {
      setBusy(true);
      try {
        await api('/api/waiting/dismiss', { key });
        toast('Вопрос убран — агент спросит снова, если это понадобится');
        onDone();
      } catch (e) {
        toast((e as Error).message);
        setBusy(false);
      }
    },
  };
}

/**
 * Ответ агенту прямо в карточке: поле «Ваш ответ» (можно голосом) и одна главная кнопка «Отправить агенту».
 * Ответ уходит агенту (api /api/waiting/answer) — он продолжает работу сам, карточка уходит из очереди.
 * «Открыть разговор» — тред этой работы с закреплённым вопросом, если хочется посмотреть, что агент делал.
 */
function AnswerBox({ item, extra }: { item: Of<'question'> | Of<'task'>; extra?: ReactNode }) {
  const ask = askOf(item);
  const qc = useQueryClient();
  const refresh = useRefreshWaiting();
  const id = useId();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const voice = useVoiceInput((heard) => setText((t) => (t.trim() ? `${t.trimEnd()} ${heard}` : heard)));
  const send = async () => {
    const answer = text.trim();
    if (!answer || busy) return;
    setBusy(true);
    try {
      const r = await answerAgent(item.key, answer);
      dropWaiting(qc, item.key);
      refresh();
      toast(
        'Агент получил ответ и продолжит',
        ask
          ? {
              label: 'Открыть разговор',
              run: () => chatStore.open({ ask: { ...ask, threadId: r.threadId, answered: true } }),
            }
          : undefined,
        8000,
      );
    } catch (e) {
      toast((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <div className="wq-answer">
      <label className="label" htmlFor={id}>
        Ваш ответ
      </label>
      <div className="wq-answer-box">
        <textarea
          id={id}
          className="input"
          rows={2}
          value={text}
          disabled={busy}
          placeholder={
            voice.state === 'rec'
              ? 'Говорите… нажмите на микрофон ещё раз, чтобы закончить'
              : voice.state === 'busy'
                ? 'Расшифровываю голосовое…'
                : 'Напишите своими словами — агент продолжит с этим'
          }
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Ctrl+Enter (⌘+Enter) — отправить; просто Enter — новая строка
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button
          type="button"
          className={`icon-btn ${voice.state === 'rec' ? 'mic-rec' : ''}`}
          aria-label={voice.state === 'rec' ? 'Закончить запись и расшифровать' : 'Сказать голосом'}
          aria-pressed={voice.state === 'rec'}
          title={voice.state === 'rec' ? 'Закончить запись' : 'Сказать голосом'}
          disabled={voice.state === 'busy' || busy}
          onClick={voice.toggle}
        >
          {voice.state === 'busy' ? <span className="spinner" /> : <Mic />}
        </button>
      </div>
      <div className="pa-row">
        <Button
          variant="primary"
          icon={busy ? <span className="spinner" /> : <Send />}
          disabled={!text.trim() || busy || voice.state !== 'idle'}
          onClick={() => void send()}
        >
          Отправить агенту
        </Button>
        {extra}
        {ask && (
          <Button
            variant="ghost"
            icon={<MessageCircle />}
            className="wq-open"
            onClick={() => chatStore.open({ ask })}
          >
            Открыть разговор
          </Button>
        )}
      </div>
    </div>
  );
}

function QuestionCard({ item, onDone }: { item: Of<'question'>; onDone: () => void }) {
  const q = item.question;
  const dismiss = useDismiss(item.key, onDone);
  const [first, ...rest] = q.questions;
  return (
    <QueueCard
      chip="Вопрос агента"
      tone="accent"
      at={`${q.label} · ${when(item.at)}`}
      title={first}
      actions={
        <AnswerBox
          item={item}
          extra={
            <Button disabled={dismiss.busy} onClick={() => void dismiss.run()}>
              Не сейчас
            </Button>
          }
        />
      }
    >
      {rest.length > 0 && (
        <ul className="prop-reasons">
          {rest.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      )}
      {q.headline && <p className="wq-ctx">Из итога: «{q.headline}»</p>}
    </QueueCard>
  );
}

function TaskCard({ item }: { item: Of<'task'> }) {
  const t = item.task;
  // Заголовок — что агент спрашивает (вопросы из итога работы по задаче, иначе его строка хода); задача — ниже
  const [first, ...rest] = t.questions;
  const title = first ?? t.progress;
  return (
    <QueueCard
      chip="Задача ждёт вас"
      tone="warning"
      at={when(item.at)}
      title={title ?? t.title}
      actions={<AnswerBox item={item} />}
    >
      {rest.length > 0 && (
        <ul className="prop-reasons">
          {rest.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      )}
      {first && t.progress && <p className="wq-text">{t.progress}</p>}
      <p className="wq-ctx">
        {title ? `Задача «${t.title}»` : 'Агент ждёт вашего ответа, чтобы продолжить эту задачу'}
      </p>
    </QueueCard>
  );
}

function BatchCard({ item }: { item: Of<'batch'> }) {
  const b = item.batch;
  return (
    <QueueCard
      chip="Батч на приёмке"
      at={when(item.at)}
      title={b.title.includes(b.offer) ? b.title : `${b.offer}: ${b.title}`}
      actions={
        <Link to="/creatives" search={{ batch: b.id }} className="btn btn-primary">
          <Clapperboard /> Смотреть батч
        </Link>
      }
    >
      <p className="wq-text">
        Готово {int(b.count)} из {int(b.target)} {plural(b.target, 'ролика', 'роликов', 'роликов')} —
        отметьте, какие оставить.
      </p>
    </QueueCard>
  );
}

function CrCard({ item }: { item: Of<'cr'> }) {
  const c = item.cr;
  return (
    <QueueCard
      chip="Правка платформы"
      at={when(item.at)}
      title={c.title}
      actions={
        <Link to="/dev/$id" params={{ id: String(c.id) }} className="btn btn-primary">
          <Code2 /> Посмотреть правку
        </Link>
      }
    >
      <p className="wq-text">
        {c.summary ? `${c.summary.slice(0, 220)}${c.summary.length > 220 ? '…' : ''}` : 'Проверка пройдена.'}{' '}
        Решите, ставить ли её.
      </p>
    </QueueCard>
  );
}

const heldText = (n: number) =>
  `${int(n)} ${plural(n, 'лид задержан', 'лида задержаны', 'лидов задержаны')} антифродом`;

function HeldCard({ item }: { item: Of<'held'> }) {
  return (
    <div className="card wq-held">
      <ShieldAlert aria-hidden="true" />
      <p>
        <b>{heldText(item.held.count)}</b>
        <span className="muted">
          {' '}
          — решите, отправлять ли в сеть. Самый давний — {when(item.held.oldestAt)}.
        </span>
      </p>
      <Link to="/leads" search={{ show: 'risky' }} className="btn btn-sm">
        Разобрать
      </Link>
    </div>
  );
}

function WaitingCard({ item, onDone }: { item: WaitingItem; onDone: () => void }) {
  switch (item.kind) {
    case 'proposal':
    case 'advice':
      return <ProposalCard p={item.proposal} onDone={onDone} />;
    case 'question':
      return <QuestionCard item={item} onDone={onDone} />;
    case 'task':
      return <TaskCard item={item} />;
    case 'batch':
      return <BatchCard item={item} />;
    case 'cr':
      return <CrCard item={item} />;
    case 'held':
      return <HeldCard item={item} />;
    case 'permission':
      return <PermissionCard p={item.permission} onDone={onDone} />;
  }
}

// ─── компактная строка — для «Сводки» ───────────────────────────────────
/** Значок заявки — по её области: бюджеты, реклама, лендинги, методика… */
const DOMAIN_ICON: Record<string, typeof Inbox> = {
  budgets: Wallet,
  ads: Megaphone,
  campaigns: Megaphone,
  landings: Globe,
  memory: BookCheck,
  creatives: Clapperboard,
  offers: Package,
};

const ICON: Record<WaitingItem['kind'], typeof Inbox> = {
  proposal: Inbox,
  advice: Lightbulb,
  question: HelpCircle,
  task: ListTodo,
  batch: Clapperboard,
  cr: Code2,
  held: ShieldAlert,
  permission: KeyRound,
};

/** Заголовок и строка пояснения строки очереди. */
function rowText(i: WaitingItem): { title: string; sub: string } {
  switch (i.kind) {
    case 'proposal':
    case 'advice':
      return {
        title: i.proposal.title,
        sub:
          i.kind === 'advice'
            ? `Совет агента · ${i.proposal.reason}`
            : `${i.dueAt ? `Ответ нужен ${inTime(i.dueAt)} · ` : ''}${i.proposal.reason}`,
      };
    case 'question':
      return {
        title: i.question.questions[0] ?? 'Вопрос агента',
        sub: `Вопрос из работы «${i.question.label}»`,
      };
    case 'task': {
      const asked = i.task.questions[0] ?? i.task.progress;
      return asked
        ? { title: asked, sub: `Задача «${i.task.title}» ждёт вашего ответа` }
        : { title: i.task.title, sub: 'Задача ждёт вашего ответа' };
    }
    case 'batch':
      return {
        title: `Батч «${i.batch.title}» на приёмке`,
        sub: `${i.batch.offer} · готово ${int(i.batch.count)} из ${int(i.batch.target)}`,
      };
    case 'cr':
      return { title: i.cr.title, sub: 'Правка платформы готова — решите, ставить ли её' };
    case 'held':
      return { title: heldText(i.held.count), sub: 'Решите, отправлять ли их в сеть' };
    case 'permission':
      return {
        title: permissionText(i.permission).title,
        sub: `Разрешение в чате · ответ нужен ${inTime(i.permission.expiresAt)}`,
      };
  }
}

function RowActions({ item, onDone }: { item: WaitingItem; onDone: () => void }) {
  const dismiss = useDismiss(item.key, onDone);
  switch (item.kind) {
    case 'proposal':
    case 'advice':
      return <ProposalActions p={item.proposal} onDone={onDone} compact />;
    case 'question':
    case 'task': {
      // В строке «Сводки» поля нет — разговор этой работы с закреплённым вопросом, первое сообщение — ответ
      const ask = askOf(item);
      return (
        <>
          {ask && (
            <Button size="sm" icon={<MessageCircle />} onClick={() => chatStore.open({ ask })}>
              Ответить
            </Button>
          )}
          {item.kind === 'question' && (
            <Button size="sm" variant="ghost" disabled={dismiss.busy} onClick={() => void dismiss.run()}>
              Не сейчас
            </Button>
          )}
        </>
      );
    }
    case 'batch':
      return (
        <Link to="/creatives" search={{ batch: item.batch.id }} className="btn btn-sm">
          Смотреть батч
        </Link>
      );
    case 'cr':
      return (
        <Link to="/dev/$id" params={{ id: String(item.cr.id) }} className="btn btn-sm">
          Посмотреть
        </Link>
      );
    case 'held':
      return (
        <Link to="/leads" search={{ show: 'risky' }} className="btn btn-sm">
          Разобрать
        </Link>
      );
    case 'permission':
      return <PermissionButtons p={item.permission} onDone={onDone} size="sm" />;
  }
}

/** Первые строки очереди с действиями прямо в строке — на «Сводке». */
export function WaitingRows({ items }: { items: WaitingItem[] }) {
  const refresh = useRefreshWaiting();
  return (
    <ul className="wr-list">
      {items.map((i) => {
        const Icon =
          (i.kind === 'proposal' || i.kind === 'advice' ? DOMAIN_ICON[i.proposal.domain] : null) ??
          ICON[i.kind];
        const t = rowText(i);
        return (
          <li key={i.key} className="wr" data-money={i.money || undefined}>
            <span className="wr-ico" aria-hidden="true">
              <Icon />
            </span>
            <div className="wr-main">
              <b>{t.title}</b>
              <span>{t.sub}</span>
            </div>
            <div className="wr-acts">
              <RowActions item={i} onDone={refresh} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ─── страница ───────────────────────────────────────────────────────────
export function DecisionsPage() {
  // Старая ссылка «Решения → История» — история теперь в журнале агента
  const search = useSearch({ strict: false }) as { tab?: string };
  const q = useWaiting();
  const refresh = useRefreshWaiting();
  if (search.tab === 'history') return <Navigate to="/agent/journal" replace />;
  const items = q.data?.items ?? [];
  const total = q.data?.total ?? 0;
  const main = items.filter((i) => i.group === 'main');
  const tech = items.filter((i) => i.group === 'tech');
  const firstAdvice = main.findIndex((i) => i.kind === 'advice');
  const advice = main.filter((i) => i.kind === 'advice').length;
  const soonest = tech.reduce<string | null>((m, i) => (i.dueAt && (!m || i.dueAt < m) ? i.dueAt : m), null);
  // Полная автономия: агент решает сам — объясняем, почему сюда всё равно что-то приходит
  const full = q.data?.autonomy === 'full';
  return (
    <>
      <PageHead
        title="Решения"
        sub={
          q.isSuccess
            ? full
              ? 'Агент действует сам, без ваших одобрений. Сюда приходит только то, что под силу лишь вам: ключи, доступы к кабинетам и бизнесу.'
              : total
                ? `Ждёт вас: ${int(total)}. Сверху — деньги и срочное.`
                : 'Всё разобрано'
            : 'Всё, что ждёт вашего решения'
        }
        actions={
          <Link to="/agent/journal" className="btn btn-ghost">
            <History /> История — в журнале
          </Link>
        }
      />
      <div className="pc dec">
        {q.isPending && <div className="card skeleton" style={{ height: 220 }} />}
        {q.isError && (
          <div className="card">
            <EmptyState
              icon={<Inbox />}
              title="Не удалось загрузить"
              action={<Button onClick={() => q.refetch()}>Повторить</Button>}
            >
              {(q.error as Error).message}
            </EmptyState>
          </div>
        )}
        {q.isSuccess && total === 0 && (
          <div className="card">
            <EmptyState
              icon={<Check />}
              title="Ничего не ждёт вас"
              action={
                <Link to="/agent/journal" className="btn">
                  Открыть журнал решений
                </Link>
              }
            >
              {full
                ? 'Агент работает сам. Когда ему понадобится то, что знаете или решаете только вы, вопрос появится здесь, на значке раздела и в Telegram. Что он сделал — в журнале.'
                : 'Когда агент захочет что-то изменить, задаст вопрос или закончит батч, это появится здесь, на значке раздела и в Telegram. Что уже решено — в журнале.'}
            </EmptyState>
          </div>
        )}
        {main.map((i, n) => (
          <div key={i.key} className="contents">
            {n === firstAdvice && (
              <h2 className="dec-h">
                <Lightbulb size={18} /> Советы агента <span className="count">{advice}</span>
              </h2>
            )}
            <WaitingCard item={i} onDone={refresh} />
          </div>
        ))}
        {tech.length > 0 && (
          <details className="wq-tech" open={main.length === 0}>
            <summary>
              <KeyRound size={18} aria-hidden="true" />
              <b>Технические разрешения</b> <span className="count">{tech.length}</span>
              {soonest && <span className="muted wq-when">ответ нужен {inTime(soonest)}</span>}
              <ChevronRight className={soonest ? 'wq-chev' : 'wq-chev end'} aria-hidden="true" />
            </summary>
            <p className="muted wq-tech-note">
              Записать файл, выполнить команду — агент ждёт ответа в чате; без ответа через 10 минут он
              продолжит без этого.
            </p>
            <div className="wq-tech-list">
              {tech.map((i) =>
                i.kind === 'permission' ? (
                  <PermissionCard key={i.key} p={i.permission} onDone={refresh} />
                ) : null,
              )}
            </div>
          </details>
        )}
      </div>
    </>
  );
}
